import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, Empty } from "@/components/Shell";

const TYPES = ["asset", "liability", "equity", "income", "expense"];
const blank = { code: "", name: "", type: "expense", subtype: "", description: "" };

export default function Accounts() {
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/accounts").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/accounts/${editing}`, form);
      else await api.post("/accounts", form);
      toast.success(editing ? "Account updated" : "Account created");
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/accounts/${id}`); toast.success("Account deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const visible = filter === "all" ? rows : rows.filter((r) => r.type === filter);

  return (
    <div data-testid="accounts-page">
      <PageHeader
        title="Chart of Accounts"
        subtitle="Uganda-ready ledger structure — assets, liabilities, equity, income and expenses."
        action={canWrite && (
          <button data-testid="add-account-button" onClick={() => { setForm(blank); setEditing(null); setOpen(true); }}
            className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
            <Plus size={16} /> New account
          </button>
        )}
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {["all", ...TYPES].map((t) => (
          <button key={t} data-testid={`filter-${t}`} onClick={() => setFilter(t)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold capitalize transition-colors ${
              filter === t ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {t}
          </button>
        ))}
      </div>

      <Panel title={`${visible.length} accounts`}>
        {visible.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="accounts-table">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-2.5">Code</th><th className="px-5 py-2.5">Name</th>
                  <th className="px-5 py-2.5">Type</th><th className="px-5 py-2.5">Sub type</th>
                  <th className="px-5 py-2.5 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {visible.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 font-mono text-xs font-semibold text-slate-900">{a.code}</td>
                    <td className="px-5 py-3 font-medium text-slate-800">{a.name}</td>
                    <td className="px-5 py-3 capitalize text-slate-600">{a.type}</td>
                    <td className="px-5 py-3 text-slate-500">{a.subtype || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      {canWrite && (
                        <div className="flex justify-end gap-2">
                          <button data-testid={`edit-account-${a.code}`} onClick={() => { setForm({ ...blank, ...a }); setEditing(a.id); setOpen(true); }}
                            className="text-slate-400 hover:text-emerald-600" aria-label="Edit account"><Pencil size={15} /></button>
                          {!a.system && (
                            <button data-testid={`delete-account-${a.code}`} onClick={() => remove(a.id)}
                              className="text-slate-400 hover:text-rose-600" aria-label="Delete account"><Trash2 size={15} /></button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty message="No accounts in this category." />}
      </Panel>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white shadow-md" data-testid="account-dialog">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="font-display text-base font-bold text-slate-900">{editing ? "Edit account" : "New account"}</h3>
              <button data-testid="close-account-dialog" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4 p-5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Code</label>
                  <input data-testid="account-code-input" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Type</label>
                  <select data-testid="account-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm capitalize focus:ring-2 focus:ring-emerald-500">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Account name</label>
                <input data-testid="account-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Sub type</label>
                <input data-testid="account-subtype-input" value={form.subtype || ""} onChange={(e) => setForm({ ...form, subtype: e.target.value })}
                  placeholder="e.g. Current Asset, Operating Expense"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <button data-testid="save-account-button" className="w-full rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
                {editing ? "Save changes" : "Create account"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
